import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {parse, subDays} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subDays(new Date(), 7),
    dtEnd: new Date(),
    vehicle: '',
    showImages: false
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    vehicle: "Veículo",
    showImages: "Exibir imagens"
};

const GarrisonDamage = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [appliedFilters, setAppliedFilters] = useState(initialFilters());
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        dateTime: {label: "Data/hora", visible: true},
        prefix: {label: "Prefixo", visible: true},
        plate: {label: "Placa", visible: true},
        user: {label: "Relatado por", visible: true},
        observation: {label: "Observação", visible: true}
    }));

    const [title] = useState("Avaria de veículos das guarnições");
    const [headerSelected, setHeaderSelected] = useState(-1);

    const buildGarrisonDamageUrlFilters = () => {
        const filters = initialFilters();

        const dtInitialParam = ReportUtils.getUrlParam("dtinitial");
        const dtFinalParam = ReportUtils.getUrlParam("dtfinal");
        const vehicleParam = ReportUtils.getUrlParam("v");

        return {
            ...filters,
            dtStart: dtInitialParam ? parse(dtInitialParam, "dd/MM/yyyy HH:mm", new Date()) : filters.dtStart,
            dtEnd: dtFinalParam ? parse(dtFinalParam, "dd/MM/yyyy HH:mm", new Date()) : filters.dtEnd,
            vehicle: vehicleParam ? { label: vehicleParam, value: vehicleParam } : filters.vehicle,
        };
    };

    const hasUrlFilters = () => {
        return ["dtinitial", "dtfinal", "v"].some(param => ReportUtils.getUrlParam(param));
    };

    const [filters, setFilters] = useState(buildGarrisonDamageUrlFilters);

    useEffect(() => {
        const loadVehicleText = async () => {
            const vehicleParam = ReportUtils.getUrlParam("v");

            if (!vehicleParam) return;

            try {
                const { data: response } = await axios.get(`${API_URL}/garrison_damage/vehicles/getAllVehicles`, {
                    params: {
                        id: vehicleParam
                    }
                });

                const vehicle = response.find(item => String(item.id) === String(vehicleParam));

                if (!vehicle) return;

                setFilters(prev => ({
                    ...prev,
                    vehicle: {
                        label: vehicle.text,
                        value: vehicle.id
                    }
                }));

            } catch (error) {
                console.error("Erro ao carregar texto do veículo", error);
            }
        };

        loadVehicleText();
    }, []);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const appliedUrlRef = useRef(false);

    useEffect(() => {
        if (!hasUrlFilters()) return;
        if (appliedUrlRef.current) return;

        appliedUrlRef.current = true;
        apply();
    }, []);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                vehicle: filters.vehicle ? filters.vehicle.value : '',
                showImages: filters.showImages ?? false
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/garrison_damage/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        ReportUtils.clearUrlFilters();
        setFilters(initialFilters());
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                visibilityColumns={true}
                                columns={tableColumns}
                                setColumns={setTableColumns}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={reportData?.length}/>
                            <Content reportData={reportData} data={reportData?.length} showImages={appliedFilters.showImages} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default GarrisonDamage;